import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { C, FONT_NUM } from './tokens';
import { fmtPct, fmtPrice, getLocalizedText, planProfileStyle } from './formatters';
import { Tag } from './atoms';

// One line inside the trade plan card.
const TradePlanRow = ({ k, v, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
    <Typography sx={{ fontSize: 10.5, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {k}
    </Typography>
    <Typography sx={{ fontFamily: FONT_NUM, fontSize: 12, fontWeight: 700, color: color || C.ink }}>
      {v}
    </Typography>
  </Box>
);

TradePlanRow.propTypes = {
  k: PropTypes.node.isRequired,
  v: PropTypes.node,
  color: PropTypes.string,
};

TradePlanRow.defaultProps = {
  v: '—',
  color: undefined,
};

// Renders the backend-provided `trade_plan` payload.
const TradePlan = ({ row, t, locale }) => {
  const plan = row?.trade_plan || null;
  if (!plan) return <Typography sx={{ fontSize: 12, color: C.mutedSoft }}>—</Typography>;
  const profileStyle = planProfileStyle(plan.plan_profile);
  let sizeColor = C.amber;
  if (plan.position_size_pct >= 90) sizeColor = C.green;
  else if (plan.position_size_pct >= 70) sizeColor = C.blue;
  return (
    <Box sx={{ minWidth: 188, lineHeight: 1.5 }}>
      <TradePlanRow
        k={t('breakoutCandidates.tradePlan.entry')}
        v={`${fmtPrice(plan.entry_low, locale)} – ${fmtPrice(plan.entry_high, locale)}`}
      />
      <TradePlanRow
        k={t('breakoutCandidates.tradePlan.stop')}
        v={`${fmtPrice(plan.stop, locale)} (${fmtPct(-plan.risk_pct, 1)})`}
        color={C.red}
      />
      <TradePlanRow
        k={t('breakoutCandidates.tradePlan.target')}
        v={`${fmtPrice(plan.target, locale)} (${fmtPct(plan.reward_pct, 1)})`}
        color={C.green}
      />
      <TradePlanRow
        k={t('breakoutCandidates.tradePlan.rr')}
        v={plan.rr ? `${plan.rr.toFixed(2)} : 1` : '—'}
        color={plan.rr >= 2 ? C.green : C.amber}
      />
      <TradePlanRow
        k={t('breakoutCandidates.tradePlan.size')}
        v={plan.position_size_pct == null ? '—' : `${plan.position_size_pct}%`}
        color={sizeColor}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 0.15 }}>
        <Typography sx={{ fontSize: 10.5, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {t('breakoutCandidates.tradePlan.profile')}
        </Typography>
        <Tag bg={profileStyle.bg} fg={profileStyle.fg} sx={{ height: 22, px: 0.8 }}>
          {getLocalizedText(plan.plan_profile_label, locale)}
        </Tag>
      </Box>
    </Box>
  );
};

TradePlan.propTypes = {
  row: PropTypes.shape({
    trade_plan: PropTypes.shape({
      entry_low: PropTypes.number,
      entry_high: PropTypes.number,
      stop: PropTypes.number,
      risk_pct: PropTypes.number,
      target: PropTypes.number,
      reward_pct: PropTypes.number,
      rr: PropTypes.number,
      position_size_pct: PropTypes.number,
      plan_profile: PropTypes.string,
      plan_profile_label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ vi: PropTypes.string, en: PropTypes.string }),
      ]),
    }),
  }),
  t: PropTypes.func.isRequired,
  locale: PropTypes.string,
};

TradePlan.defaultProps = {
  row: null,
  locale: 'en-US',
};

export default TradePlan;
